import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, API_BASE } from '../lib/api';
import {
  PageTitle, Card, Btn, Pill, Section, Kpi,
  Table, Th, Td, fmt, FairnessPill, ScoreChip, DimensionBars,
} from '../components/ui-kit';
import { ExternalLink, FileSignature, ListChecks } from 'lucide-react';

interface Candidate {
  rank: number;
  blind_id: string;
  match_score: number;
  fairness_grade?: string;
  dimension_scores?: Record<string, number>;
  explanation?: string;
  title?: string;
  seniority?: string;
  years_experience?: number;
  skills_top5?: string[];
  certifications?: string[];
  domains?: string[];
  impact_top3?: string[];
  city_zone?: string;
  available_from?: string;
  wolf_tier?: string;
  matched_at?: string;
}
interface CandidatesResp {
  mission_id: string;
  engine: string;
  count: number;
  candidates: Candidate[];
}

export default function MissionDetail() {
  const { id } = useParams();
  const { getToken } = useXyqarAuth();
  const [data, setData] = React.useState<CandidatesResp | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const tok = await getToken().catch(() => '');
        const j = await apiGet<CandidatesResp>(`/api/v1/client/missions/${id}/candidates`, tok);
        setData(j);
      } catch (e: any) {
        setError(e.message);
        setData({ mission_id: id ?? '', engine: '', count: 0, candidates: [] });
      }
    })();
  }, [id, getToken]);

  const top = data?.candidates ?? [];
  const avgScore = top.length
    ? Math.round((top.reduce((s, c) => s + (c.match_score ?? 0), 0) / top.length) * 100)
    : 0;
  const greens = top.filter((c) => c.fairness_grade === 'GREEN').length;

  return (
    <div>
      <PageTitle
        kicker={`Mission · ${id}`}
        title={top[0]?.title ? `${top[0].title} (top match)` : 'Mission detail'}
        right={
          <div className="flex items-center gap-2">
            <Pill tone="live">{data?.engine || 'wolf_brain_v3'}</Pill>
            <Pill>{fmt(data?.count)} ranked</Pill>
          </div>
        }
      />

      {error && (
        <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
          <p className="text-amber-300 text-xs font-mono">{error}</p>
        </Card>
      )}

      <Section title="Match summary">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="blue"  label="Ranked candidates" value={fmt(top.length)} />
          <Kpi accent="green" label="Avg Wolf score"    value={`${avgScore}%`} />
          <Kpi accent="green" label="Fairness GREEN"    value={fmt(greens)} hint={`of ${top.length}`} />
          <Kpi              label="Engine"             value="v3" hint="deterministic, no LLM" />
        </div>
      </Section>

      <Section
        title="Top candidates"
        hint="Anonymous handles. 8 dimensions persisted per match. Open the HTML fiche for the printable card."
        right={
          <div className="flex items-center gap-2">
            <Link to={`/missions/${id}/cra`}><Btn><ListChecks className="h-3 w-3" /> CRA</Btn></Link>
            <Link to={`/missions/${id}/docs`}><Btn><FileSignature className="h-3 w-3" /> E2E Docs</Btn></Link>
          </div>
        }
      >
        {top.length === 0 && (
          <Card><p className="text-white/40 text-sm">No match persisted yet. Trigger a match via your CSM, or run a programmatic match call.</p></Card>
        )}
        {top.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Wolf ID</Th>
                <Th>Title</Th>
                <Th>Wolf score</Th>
                <Th>Fairness</Th>
                <Th>Stack</Th>
                <Th>Matched</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {top.map((c) => (
                <tr key={c.blind_id} className="border-t border-white/5 hover:bg-white/5 transition">
                  <Td><span className="text-white/40 font-mono">{c.rank}</span></Td>
                  <Td>
                    <span className="text-white font-mono text-xs">{c.blind_id}</span>
                    <div className="text-[10px] text-white/30 font-mono">tier {c.wolf_tier ?? '—'} · {c.seniority ?? '—'}</div>
                  </Td>
                  <Td>
                    <span className="text-white text-xs">{c.title ?? '—'}</span>
                    <div className="text-[10px] text-white/30 font-mono">{c.years_experience ?? '?'} y · {c.city_zone ?? '—'}</div>
                  </Td>
                  <Td><ScoreChip score={c.match_score} /></Td>
                  <Td><FairnessPill grade={c.fairness_grade} /></Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {(c.skills_top5 ?? []).slice(0, 3).map((s) => (
                        <span key={s} className="text-[10px] font-mono text-white/60 bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  </Td>
                  <Td><span className="text-[11px] text-white/40 font-mono">{c.matched_at?.slice(0, 16).replace('T', ' ')}</span></Td>
                  <Td className="text-right">
                    <Btn as="a" href={`${API_BASE}/api/v1/client/blind/${c.blind_id}/fiche.html`}>
                      Fiche <ExternalLink className="h-3 w-3" />
                    </Btn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      {top.length > 0 && (
        <Section title="Top match · 8-dimension breakdown" hint={`Wolf Brain v3 deterministic scoring — ${top[0].blind_id}`}>
          <Card>
            <DimensionBars dims={top[0].dimension_scores} />
            {top[0].explanation && (
              <blockquote className="mt-6 border-l-2 border-white/20 pl-4 italic text-white/70 text-sm">
                {top[0].explanation}
              </blockquote>
            )}
            <div className="mt-6 flex flex-wrap gap-2">
              {(top[0].impact_top3 ?? []).map((imp, i) => (
                <span key={i} className="text-[11px] text-white/70 bg-white/5 border border-white/10 px-2 py-1 rounded">• {imp}</span>
              ))}
            </div>
          </Card>
        </Section>
      )}
    </div>
  );
}
